import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../../apiConfig';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const AiAddAssessmentPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    jobDescription: '',
    assessmentType: 'Competency', // Default value
    difficultyLevel: '', // Will store 'Easy', 'Medium', or 'Hard'
    numberOfQuestions: 10, // Default value
    hours: '',
    minutes: '',
    maxTries: '',
  });
  const [error, setError] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [difficultyOptions, setDifficultyOptions] = useState([]);
  const navigate = useNavigate();

  // Load adminData from sessionStorage on mount
  useEffect(() => {
    const storedAdminData = JSON.parse(sessionStorage.getItem('adminData'));
    console.log('Loaded adminData:', storedAdminData);
    setAdminData(storedAdminData);
  }, []);
  useEffect(() => {
  fetch(`${API_URL}/api/v1/difficultlevel`)
    .then((res) => res.json())
    .then((data) => setDifficultyOptions(data))
    .catch((err) => console.error('Error fetching difficulty levels:', err));
}, []);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  // Handle CKEditor description changes
  const handleDescriptionChange = (event, editor) => {
    const data = editor.getData();
    console.log('CKEditor description data:', data);
    setFormData((prev) => ({ ...prev, description: data }));
    setError(null);
  };

  // Handle CKEditor instructions changes
  const handleInstructionsChange = (event, editor) => {
    const data = editor.getData();
    console.log('CKEditor instructions data:', data);
    setFormData((prev) => ({ ...prev, instructions: data }));
    setError(null);
  };

  // Handle CKEditor job description changes
  const handleJobDescriptionChange = (event, editor) => {
    const data = editor.getData();
    console.log('CKEditor job description data:', data);
    setFormData((prev) => ({ ...prev, jobDescription: data }));
    setError(null);
  };

  // Handle difficulty level checkbox changes
  const handleDifficultyChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      difficultyLevel: prev.difficultyLevel === value ? '' : value, // Toggle selection
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const duration = parseInt(formData.hours || 0) * 60 + parseInt(formData.minutes || 0);
    const assessmentData = {
      title: formData.title,
      description: formData.description,
      instructions: formData.instructions,
      jobDescription: formData.jobDescription,
      assessmentType: formData.assessmentType,
      difficultyLevel: formData.difficultyLevel,
      numberOfQuestions: parseInt(formData.numberOfQuestions) || 10, // Default to 10
      duration,
      maxTries: parseInt(formData.maxTries) || 1, // Default to 1
      creatorId: adminData?.id,
      status: 0, // Send numeric status for draft
    };
    console.log('Submitting assessment:', assessmentData);

    try {
      if (!adminData?.id) {
        throw new Error('Admin ID not found. Please log in again.');
      }

      const response = await fetch(`${API_URL}/api/v1/admin/aiassessments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(assessmentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to create assessment');
      }

      const result = await response.json();
      console.log('Assessment Created:', result);
      navigate('/admin/assessments');
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    }
  };

  // Generate options for hours (0-23), minutes (0-59), and max tries (1-10)
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i);
  const maxTryOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6 md:p-8">
      {/* Header Section */}
      <div className="mx-auto mb-8">
        <h1 className="mt-4 text-3xl font-bold text-gray-800">Create AI Assessment</h1>
      </div>

      {/* Form Section */}
      <div className="mx-auto bg-white rounded-2xl shadow-lg p-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-center font-medium">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {/* Title Field */}
          <div className="mb-6">
            <label
              htmlFor="title"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Assessment Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-gray-50 text-gray-800"
              placeholder="Enter assessment title"
              required
            />
          </div>

          {/* Description Field with CKEditor */}
          <div className="mb-6">
            <label
              htmlFor="description"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Assessment Description
            </label>
            <div className="border border-gray-200 rounded-lg bg-gray-50">
              <CKEditor
                editor={ClassicEditor}
                data={formData.description}
                onReady={(editor) => {
                  console.log('CKEditor description initialized:', editor);
                  editor.editing.view.change((writer) => {
                    writer.setStyle(
                      'min-height',
                      '300px',
                      editor.editing.view.document.getRoot()
                    );
                  });
                }}
                onChange={handleDescriptionChange}
                onError={(error) => {
                  console.error('CKEditor description error:', error);
                  setError('Failed to load the description editor. Please try again.');
                }}
                config={{
                  toolbar: [
                    'heading',
                    '|',
                    'bold',
                    'italic',
                    'underline',
                    'strikethrough',
                    '|',
                    'link',
                    'bulletedList',
                    'numberedList',
                    'blockQuote',
                    '|',
                    'insertTable',
                    'undo',
                    'redo',
                  ],
                  heading: {
                    options: [
                      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                    ],
                  },
                  placeholder: 'Enter a detailed description of the assessment...',
                }}
              />
            </div>
          </div>

          {/* Instructions Field with CKEditor */}
          <div className="mb-6">
            <label
              htmlFor="instructions"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Assessment Instructions
            </label>
            <div className="border border-gray-200 rounded-lg bg-gray-50">
              <CKEditor
                editor={ClassicEditor}
                data={formData.instructions}
                onReady={(editor) => {
                  console.log('CKEditor instructions initialized:', editor);
                  editor.editing.view.change((writer) => {
                    writer.setStyle(
                      'min-height',
                      '300px',
                      editor.editing.view.document.getRoot()
                    );
                  });
                }}
                onChange={handleInstructionsChange}
                onError={(error) => {
                  console.error('CKEditor instructions error:', error);
                  setError('Failed to load the instructions editor. Please try again.');
                }}
                config={{
                  toolbar: [
                    'heading',
                    '|',
                    'bold',
                    'italic',
                    'underline',
                    'strikethrough',
                    '|',
                    'link',
                    'bulletedList',
                    'numberedList',
                    'blockQuote',
                    '|',
                    'insertTable',
                    'undo',
                    'redo',
                  ],
                  heading: {
                    options: [
                      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                    ],
                  },
                  placeholder: 'Enter detailed instructions for the assessment...',
                }}
              />
            </div>
          </div>

          {/* Job Description Field with CKEditor */}
          <div className="mb-6">
            <label
              htmlFor="jobDescription"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Job Description
            </label>
            <div className="border border-gray-200 rounded-lg bg-gray-50">
              <CKEditor
                editor={ClassicEditor}
                data={formData.jobDescription}
                onReady={(editor) => {
                  console.log('CKEditor job description initialized:', editor);
                  editor.editing.view.change((writer) => {
                    writer.setStyle(
                      'min-height',
                      '300px',
                      editor.editing.view.document.getRoot()
                    );
                  });
                }}
                onChange={handleJobDescriptionChange}
                onError={(error) => {
                  console.error('CKEditor job description error:', error);
                  setError('Failed to load the job description editor. Please try again.');
                }}
                config={{
                  toolbar: [
                    'heading',
                    '|',
                    'bold',
                    'italic',
                    'underline',
                    'strikethrough',
                    '|',
                    'link',
                    'bulletedList',
                    'numberedList',
                    'blockQuote',
                    '|',
                    'insertTable',
                    'undo',
                    'redo',
                  ],
                  heading: {
                    options: [
                      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                    ],
                  },
                  placeholder: 'Enter the job description...',
                }}
              />
            </div>
          </div>

          {/* Assessment Type Field */}
          <div className="mb-6">
            <label
              htmlFor="assessmentType"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Assessment Type
            </label>
           <input
              type="text"
              id="assessmentType"
              name="assessmentType"
              value={formData.assessmentType}
              onChange={(e) => setFormData({ ...formData, assessmentType: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-800"
            />

          </div>

          {/* Difficulty Level Field */}
         <div className="mb-6">
  <label className="block text-gray-700 text-sm font-semibold mb-2">
    Difficulty Level
  </label>
  <select
    name="difficultyLevel"
    value={formData.difficultyLevel}
    onChange={(e) => handleDifficultyChange(e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
  >
    <option value="">Select difficulty</option>
    {difficultyOptions.map((level) => (
      <option key={level._id} value={level.name}>
        {level.name}
      </option>
    ))}
  </select>
</div>



          {/* Number of Questions Field */}
          <div className="mb-6">
            <label
              htmlFor="numberOfQuestions"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Number of Questions
            </label>
            <input
              type="number"
              id="numberOfQuestions"
              name="numberOfQuestions"
              value={formData.numberOfQuestions}
              onChange={handleInputChange}
              min="1"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-gray-50 text-gray-800"
              placeholder="Enter number of questions"
              required
            />
          </div>

          {/* Duration Field */}
          <div className="mb-8">
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Assessment Duration
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="hours"
                  className="block text-gray-600 text-sm font-medium mb-2"
                >
                  Hours
                </label>
                <select
                  id="hours"
                  name="hours"
                  value={formData.hours}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-gray-50 text-gray-800"
                  required
                >
                  <option value="" disabled>
                    Select hours
                  </option>
                  {hourOptions.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="minutes"
                  className="block text-gray-600 text-sm font-medium mb-2"
                >
                  Minutes
                </label>
                <select
                  id="minutes"
                  name="minutes"
                  value={formData.minutes}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-gray-50 text-gray-800"
                  required
                >
                  <option value="" disabled>
                    Select minutes
                  </option>
                  {minuteOptions.map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Maximum Tries Field */}
          <div className="mb-8">
            <label
              htmlFor="maxTries"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Maximum Attempts
            </label>
            <select
              id="maxTries"
              name="maxTries"
              value={formData.maxTries}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-gray-50 text-gray-800"
              required
            >
              <option value="" disabled>
                Select maximum attempts
              </option>
              {maxTryOptions.map((tryCount) => (
                <option key={tryCount} value={tryCount}>
                  {tryCount}
                </option>
              ))}
            </select>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/assessments')}
              className="px-6 py-2 text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 hover:border-gray-300 transition-colors duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Create Assessment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AiAddAssessmentPage;